/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { Observable } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { EventTicketAdminParticipateRequest, EventTicketAdminRegisterRequest } from 'src/app/shared/models/admin/members.model';
import { environment } from '../../../../environments/environment';
import { CalendarHelper } from '../../../shared/helpers/calendar-helper';
import { Resource } from '../../../shared/models';
import { RestClientService } from '../../../shared/services/rest-client.service';
import { ObservableResult } from '../../../shared/store';
import {
  EventCardTime,
  EventCardTypeRequest,
  EventRecurrenceRequest,
  EventReminderRequest,
  EventSessionRequest,
  EventTickets,
  EventTicketsRequest,
} from '../../resource/store/admin/resource-event-admin.state.model';
import { EVENT_ALREADY_REGISTERED_ERROR, EventCardsDataService } from './data.service';

const QR_CHECK_IN_WINDOW_CLOSED_MESSAGE = 'valid time range';
const QR_CHECK_IN_CODE_MISMATCH_MESSAGE = 'code mismatch';

@Injectable()
export class ApiEventCardsDataService implements EventCardsDataService {
  constructor(
    private client: RestClientService,
    private translocoService: TranslocoService,
  ) {}

  setEventCardTypes(eventUid: string, request: EventCardTypeRequest): ObservableResult<EventCardTypeRequest> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `events/${eventUid}/types`);
    return this.client.put<EventCardTypeRequest>(url, request).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUpdateEventType'))),
    );
  }

  setEventCardDates(eventUid: string, request: EventCardTime): ObservableResult<Resource> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `events/${eventUid}/times`);
    return this.client.put<Resource>(url, request).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUpdateEventTimes'))),
    );
  }

  setEventTickets(eventUid: string, request: EventTicketsRequest): ObservableResult<EventTickets> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `events/${eventUid}/tickets`);
    return this.client.put<EventTickets>(url, request).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUpdateEventTickets'))),
    );
  }

  deleteEventTickets(eventUid: string): ObservableResult<EventTickets> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `events/${eventUid}/tickets`);
    return this.client.delete<EventTickets>(url).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorDeleteEventTickets'))),
    );
  }

  setEventReminders(eventUid: string, request: EventReminderRequest): ObservableResult<Resource> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `events/${eventUid}/reminders`);
    return this.client.put<Resource>(url, request).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUpdateEventReminders'))),
    );
  }

  registerToEvent(eventUid: string): ObservableResult<Resource> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `events/${eventUid}/tickets/register`);
    return this.client.post<Resource>(url, {}).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError((err) =>
        ObservableResult.ofError(
          err?.status === 409
            ? EVENT_ALREADY_REGISTERED_ERROR
            : this.translocoService.translate('translations.errors.errorRegisterToEvent'),
        ),
      ),
    );
  }

  unregisterFromEvent(eventUid: string): ObservableResult<Resource> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `events/${eventUid}/tickets/unregister`);
    return this.client.post<Resource>(url, {}).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUnRegisterFromEvent'))),
    );
  }

  attendToEvent(eventUid: string): ObservableResult<Resource> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `events/${eventUid}/tickets/participate`);
    return this.client.post<Resource>(url, {}).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorAttendToEvent'))),
    );
  }

  attendToEventFromQrCode(eventUid: string, participationCode: string): ObservableResult<Resource> {
    const url = Location.joinWithSlash(
      environment.apiRootUrl || '',
      `events/${eventUid}/tickets/participate?code=` + encodeURIComponent(participationCode),
    );
    return this.client.post<Resource>(url, {}).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError((err) => ObservableResult.ofError(this.translocoService.translate(this.getQrCheckInErrorKey(err)))),
    );
  }

  adminRegisterToEventBulk(eventUid: string, request: EventTicketAdminRegisterRequest): ObservableResult<EventTickets> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `events/${eventUid}/tickets/bulk/register`);
    return this.client.post<EventTickets>(url, request).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorAdminRegisterToEvent'))),
    );
  }

  adminUnregisterFromEventBulk(eventUid: string, usersUids: string[]): ObservableResult<EventTickets> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `events/${eventUid}/tickets/bulk/unregister`);
    return this.client.post<EventTickets>(url, usersUids).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorAdminUnregisterFromEvent'))),
    );
  }

  adminAttendToEventBulk(eventUid: string, request: EventTicketAdminParticipateRequest): ObservableResult<EventTickets> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `events/${eventUid}/tickets/bulk/participate`);
    return this.client.post<EventTickets>(url, request).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorAdminAttendToEvent'))),
    );
  }

  adminMoveToWaitingListEventBulk(eventUid: string, usersUids: string[]): ObservableResult<EventTickets> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `events/${eventUid}/tickets/bulk/waiting-list`);
    return this.client.post<EventTickets>(url, usersUids).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorAdminMoveToWaitingList'))),
    );
  }

  registerToSession(eventUid: string, sessionId: string): ObservableResult<Resource> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `events/${eventUid}/tickets/register`);
    return this.client.post<Resource>(url, { sessionId }).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorRegisterToEvent'))),
    );
  }

  unregisterFromSession(eventUid: string, sessionId: string): ObservableResult<Resource> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `events/${eventUid}/tickets/unregister`);
    return this.client.post<Resource>(url, { sessionId }).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUnRegisterFromEvent'))),
    );
  }

  createSession(eventUid: string, request: EventSessionRequest): ObservableResult<Resource> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `events/${eventUid}/sessions`);
    return this.client.post<Resource>(url, request).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorCreateEventSession'))),
    );
  }

  updateSession(eventUid: string, sessionId: string, request: EventSessionRequest): ObservableResult<Resource> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `events/${eventUid}/sessions/${sessionId}`);
    return this.client.put<Resource>(url, request).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUpdateEventSession'))),
    );
  }

  deleteSession(eventUid: string, sessionId: string): ObservableResult<Resource> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `events/${eventUid}/sessions/${sessionId}`);
    return this.client.delete<Resource>(url).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError((err) => {
        const status = err?.status ?? err?.error?.status;
        if (status === 400) {
          return ObservableResult.ofError(this.translocoService.translate('translations.errors.errorDeleteEventSessionHasRegistrations'));
        }
        return ObservableResult.ofError(this.translocoService.translate('translations.errors.errorDeleteEventSession'));
      }),
    );
  }

  cancelSession(eventUid: string, sessionId: string): ObservableResult<Resource> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `events/${eventUid}/sessions/${sessionId}/cancel`);
    return this.client.post<Resource>(url, {}).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorCancelEventSession'))),
    );
  }

  setRecurrenceRule(eventUid: string, request: EventRecurrenceRequest): ObservableResult<Resource> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `events/${eventUid}/recurrence`);
    return this.client.put<Resource>(url, request).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorSetEventRecurrence'))),
    );
  }

  deleteRecurrenceRule(eventUid: string): ObservableResult<Resource> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `events/${eventUid}/recurrence`);
    return this.client.delete<Resource>(url).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorDeleteEventRecurrence'))),
    );
  }

  exportEventCalendarEvent(eventUid: string, languageCode?: string): Observable<string> {
    const headers = CalendarHelper.getDefaultCalendarRequestHeaders(languageCode);
    return this.client
      .post<string>(Location.joinWithSlash(environment.apiRootUrl || '', `events/${eventUid}/export/ics`), {}, {}, headers, 'text')
      .pipe(
        map((response) => response.body as string),
        catchError((error) => {
          console.error('Failed to download ICS', error);
          throw error;
        }),
      );
  }

  private getQrCheckInErrorKey(err: HttpErrorResponse): string {
    const message: string = err?.error?.message ?? '';
    if (err?.status === 409) {
      return 'translations.qrcode.errors.eventNotPublished';
    }
    if (err?.status === 400 && message.includes(QR_CHECK_IN_WINDOW_CLOSED_MESSAGE)) {
      return 'translations.qrcode.errors.checkInWindowClosed';
    }
    if (err?.status === 400 && message.includes(QR_CHECK_IN_CODE_MISMATCH_MESSAGE)) {
      return 'translations.qrcode.errors.invalidCode';
    }
    return 'translations.errors.errorAttendToEvent';
  }
}
